import { useEffect, type ReactElement, type ReactNode } from 'react'
import { MsalProvider } from '@azure/msal-react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { ErrorBoundary } from 'react-error-boundary'

import { queryClient } from '@/api/query-client'
import { msalInstance } from '@/lib/auth/msal'
import { env } from '@/lib/env'
import { reportError } from '@/lib/logger'
import { ErrorFallback } from '@/components/common/ErrorFallback'
import { Toaster } from '@/components/ui/toaster'
import { useUiStore } from '@/stores/ui.store'

function useThemeEffect(): void {
  const theme = useUiStore((state) => state.theme)

  useEffect(() => {
    const media = globalThis.matchMedia('(prefers-color-scheme: dark)')
    const apply = (): void => {
      const isDark = theme === 'dark' || (theme === 'system' && media.matches)
      document.documentElement.classList.toggle('dark', isDark)
    }

    apply()
    media.addEventListener('change', apply)
    return () => {
      media.removeEventListener('change', apply)
    }
  }, [theme])
}

export function AppProviders({ children }: { children: ReactNode }): ReactElement {
  useThemeEffect()

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        reportError(error, { module: 'root', componentStack: info.componentStack })
      }}
    >
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          {children}
          <Toaster />
          {env.isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
        </QueryClientProvider>
      </MsalProvider>
    </ErrorBoundary>
  )
}
